import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { VendorsProvider } from './context/VendorsContext';
import { WebsiteContentProvider } from './context/WebsiteContentContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import VendorsList from './pages/VendorsList';
import VendorForm from './pages/VendorForm';
import WebsiteContent from './pages/WebsiteContent';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import ImportTemplateManager from './pages/ImportTemplateManager';
import VendrProviders from './vendr/VendrProviders';
import VendrDashboard from './vendr/pages/VendrDashboard';
import VendrSetup from './vendr/pages/VendrSetup';
import VendrProductsList from './vendr/pages/VendrProductsList';
import VendrProductForm from './vendr/pages/VendrProductForm';
import VendrSettings from './vendr/pages/VendrSettings';
import AmazonBrandRegistryForm from './vendr/pages/AmazonBrandRegistryForm';
import AmazonProductForm from './vendr/pages/AmazonProductForm';
import VendrAmazonImportForm from './vendr/pages/VendrAmazonImportForm';
import ClientsList from './pages/ClientsList';
import ClientDetail from './pages/ClientDetail';
import ClientProductDetail from './pages/ClientProductDetail';
import InviteHistory from './pages/InviteHistory';
import StakeholdersList from './pages/StakeholdersList';
import ChangePassword from './pages/ChangePassword';
import ResetPassword from './pages/ResetPassword';
import Register from './pages/Register';
import './App.css';

const LoadingScreen = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    Loading...
  </div>
);

/** Protects dashboard routes: admin and stakeholder can access; stakeholder is redirected to /clients from admin-only pages by those pages. */
const AdminRoute = ({ children }) => {
  const { isAuthenticated, loading, role } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (role == null) return <Navigate to="/login" replace />;
  if (role !== 'admin' && role !== 'stakeholder') return <Navigate to="/vendr" replace />;
  return children;
};

/** Admin only: stakeholder gets redirected to dashboard home (they only see limited dashboard + clients). */
const AdminOnlyRoute = ({ children }) => {
  const { isAuthenticated, loading, role } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (role == null) return <Navigate to="/login" replace />;
  if (role === 'stakeholder') return <Navigate to="/" replace />;
  if (role !== 'admin') return <Navigate to="/vendr" replace />;
  return children;
};

const VendrRoute = ({ children }) => {
  const { isAuthenticated, loading, role } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (role == null) return <Navigate to="/login" replace />;
  if (role === 'stakeholder') return <Navigate to="/" replace />;
  if (role !== 'client') return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <VendorsProvider>
        <WebsiteContentProvider>
          <Router>
            <div className="App">
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/register/:id" element={<Register />} />
                <Route path="/change-password" element={<ChangePassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                <Route path="/" element={<AdminRoute><Dashboard /></AdminRoute>} />
                <Route path="/vendors" element={<AdminOnlyRoute><VendorsList /></AdminOnlyRoute>} />
                <Route path="/vendors/new" element={<AdminOnlyRoute><VendorForm /></AdminOnlyRoute>} />
                <Route path="/vendors/edit/:id" element={<AdminOnlyRoute><VendorForm /></AdminOnlyRoute>} />
                <Route path="/website" element={<AdminOnlyRoute><WebsiteContent /></AdminOnlyRoute>} />
                <Route path="/analytics" element={<AdminOnlyRoute><Analytics /></AdminOnlyRoute>} />
                <Route path="/clients" element={<AdminRoute><ClientsList /></AdminRoute>} />
                <Route path="/clients/invites" element={<AdminOnlyRoute><InviteHistory /></AdminOnlyRoute>} />
                <Route path="/stakeholders" element={<AdminOnlyRoute><StakeholdersList /></AdminOnlyRoute>} />
                <Route path="/clients/:id" element={<AdminRoute><ClientDetail /></AdminRoute>} />
                <Route path="/clients/:clientId/product/:productId" element={<AdminRoute><ClientProductDetail /></AdminRoute>} />
                <Route path="/settings" element={<AdminOnlyRoute><Settings /></AdminOnlyRoute>} />
                <Route path="/import-templates" element={<AdminOnlyRoute><ImportTemplateManager /></AdminOnlyRoute>} />

                <Route path="/vendr" element={<VendrRoute><VendrProviders /></VendrRoute>}>
                  <Route index element={<VendrDashboard />} />
                  <Route path="setup" element={<VendrSetup />} />
                  <Route path="products" element={<VendrProductsList />} />
                  <Route path="products/new" element={<VendrProductForm />} />
                  <Route path="products/edit/:id" element={<VendrProductForm />} />
                  <Route path="settings" element={<VendrSettings />} />
                  <Route path="amazon-registry" element={<AmazonBrandRegistryForm />} />
                  <Route path="amazon-products/new" element={<AmazonProductForm />} />
                  <Route path="amazon-products/edit/:id" element={<AmazonProductForm />} />
                  <Route path="amazon-products/import" element={<VendrAmazonImportForm />} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </Router>
        </WebsiteContentProvider>
      </VendorsProvider>
    </AuthProvider>
  );
}

export default App;

